// miniapp/src/components/chat/MessageDeleteSheet.tsx
//
// v4.1 milestone — iOS-style bottom action sheet shown after the technician
// taps the floating trash chip in multi-select mode (batch delete).
// Single-message deletes use the contextual menu inside MessageActionModal
// instead — this surface is BATCH-ONLY.
//
// Single destructive action: "Delete for me and {userName}" (or just
// "Delete for everyone" if no name is provided). Selection mode already
// hides the checkbox on rows that can't be unsent on Telegram, so every
// message in the batch is guaranteed deletable — no two-mode branching
// here.
//
// Confirmation prompt only when the batch is >= 5 (scope decision C2).

import { useEffect, useState } from 'react';
import type { Message } from '../../types/ticket';

const CONFIRM_THRESHOLD = 5;

export interface MessageDeleteSheetProps {
  /** The messages targeted by this batch delete action. */
  messages: Message[];
  /** Parent calls the API + handles result. */
  onConfirm: () => void;
  onClose: () => void;
}

export function MessageDeleteSheet({ messages, onConfirm, onClose }: MessageDeleteSheetProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Slide-up + fade-in on mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const reveal = {
    transform: mounted ? 'translateY(0)' : 'translateY(20px)',
    opacity: mounted ? 1 : 0,
    transition:
      'transform 220ms cubic-bezier(0.32, 0.72, 0, 1), opacity 200ms ease-out',
  } as const;

  const requiresConfirm = messages.length >= CONFIRM_THRESHOLD;
  const commit = () => {
    if (requiresConfirm) {
      if (!window.confirm(`Delete ${messages.length} messages?`)) return;
    }
    onConfirm();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-end safe-area-bottom"
      onClick={onClose}
      style={{ touchAction: 'none' }}
    >
      <div
        className="absolute inset-0 bg-black/30"
        style={{
          opacity: mounted ? 1 : 0,
          transition: 'opacity 200ms ease-out',
        }}
      />

      <div
        className="relative mb-2 rounded-2xl bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          ...reveal,
          width: 'min(340px, calc(100vw - 48px))',
        }}
      >
        <button
          type="button"
          onClick={commit}
          className="w-full px-4 py-4 text-center text-base text-red-500 hover:bg-gray-50 active:bg-gray-100"
        >
          Delete
        </button>
      </div>

      <div
        className="relative mb-2 mt-2 rounded-2xl bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          ...reveal,
          width: 'min(340px, calc(100vw - 48px))',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-4 text-center text-base font-semibold text-[#007AFF] hover:bg-gray-50 active:bg-gray-100"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
